import {
  computed,
  effect,
  inject,
  Injectable,
  linkedSignal,
  resource,
  signal,
  type Signal,
  untracked,
} from '@angular/core';
import {
  EpubNavigator,
  EpubPreferences,
  type EpubNavigatorConfiguration,
  type EpubNavigatorListeners,
  type FXLFramePoolManager,
  type IEpubPreferences,
  type KeyboardPeripheralEventData,
} from '@readium/navigator';
import type { BasicTextSelection, FrameClickEvent } from '@readium/navigator-html-injectables';
import { Layout, Link, Locator, LocatorLocations, Profile, Publication } from '@readium/shared';
import { AuthStore } from '../../../core/auth/auth-store';
import { CHAPTER_START_PROGRESSION } from '../../../helpers/locator';
import {
  applyDocumentTheme,
  findReaderThemeByBackgroundColor,
} from '../../../helpers/reader-themes';
import { findVisibleIframe, getSelectionContext } from '../../../helpers/readium';
import { isIframe } from '../../../helpers/utils';
import { customFetch } from '../../../libs/CustomFetch';
import { CustomHttpFetcher } from '../../../libs/CustomHttpFetcher';
import { OfflineResourceInliner } from '../../../libs/OfflineResourceInliner';
import type { MetadataResponse } from '../../../models/metadata-response';
import type { Highlight, SelectionContext } from '../../../models/selections';
import { ControlsVisibility } from '../../controls-visibility/controls-visibility';
import { FeatureFlagsService } from '../../feature-flags/feature-flags';
import { FullscreenService } from '../../fullscreen/fullscreen';
import { MetadataService } from '../../metadata/metadata';
import { BookContentCache } from '../../offline/book-content-cache';
import { bookFilesPrefix, bookFilesRootUrl } from '../../offline/offline-keys';
import { OfflineSessionService } from '../../offline/offline-session';
import { PaymentStatusService } from '../../payment-status/payment-status';
import { BookAnalysisService } from '../book-analysis';
import { ChapterService } from '../chapter/chapter';
import { ReaderLocationService } from '../reader-location';
import { CustomFontsService } from '../settings/custom-fonts';

interface ResolvedPosition {
  locator: Locator;
  chapterChanged: boolean;
  resetColumn: boolean;
}

/**
 * Decides whether the reader may move forward. A refusing guard is expected to tell the reader why
 * (by opening the pending question, warning them or asking them to confirm); the navigation is then
 * abandoned, and `proceed` carries it out after all if the reader confirms.
 */
export type ForwardNavigationGuard = (kind: 'pageTurn' | 'jump', proceed: () => void) => boolean;

function sameHrefs(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((href, index) => href === b[index]);
}

@Injectable({
  providedIn: 'root',
})
export class ReaderNavigatorService {
  private static readonly EMPTY_PROGRESS = 0;
  private static readonly FULL_PROGRESS = 100;
  private static readonly DEBOUNCE_SAVE_LAST_LOCATION = 1000;
  private static readonly CONTEXT_NUM_WORDS = 10;
  private static readonly MIN_SINGLE_COL_WIDTH = 320;
  private static readonly TWO_COLUMNS = 2;
  private static readonly DEFAULT_FXL_ZOOM = 1;
  private static readonly DEFAULT_FONT_SIZE = 1;
  private static readonly DEFAULT_LINE_HEIGHT = 1.5;
  // Defers the rollback out of Readium's syncLocation call stack.
  private static readonly ROLLBACK_DELAY = 0;
  // Frames Readium needs to re-lay out a fixed-layout spread after a resize or a preference change.
  private static readonly LAYOUT_SETTLE_FRAMES = 2;

  private static readonly NAV_PERIPHERAL_TYPE = 'page-navigation';
  private static readonly KEY_CODE_ARROW_LEFT = 37;
  private static readonly KEY_CODE_ARROW_RIGHT = 39;
  private static readonly LINK_COLOR_LIGHT = 'rgba(0, 0, 0, 0.9)';
  private static readonly CONTENT_STYLE_ID = 'nr-content-styles';
  // Readium turns the page on a tap in the last of these horizontal slices of the frames.
  private static readonly EDGE_TAP_SLICES = 4;

  private readonly authStore = inject(AuthStore);
  private readonly bookAnalysisService = inject(BookAnalysisService);
  private readonly chapterService = inject(ChapterService);
  private readonly readerLocationService = inject(ReaderLocationService);
  private readonly customFontsService = inject(CustomFontsService);
  private readonly metadataService = inject(MetadataService);
  private readonly controlsVisibility = inject(ControlsVisibility);
  private readonly featureFlags = inject(FeatureFlagsService);
  private readonly fullscreenService = inject(FullscreenService);
  private readonly paymentStatusService = inject(PaymentStatusService);
  private readonly offlineSession = inject(OfflineSessionService);
  private readonly bookContentCache = inject(BookContentCache);

  private readonly metadata = this.metadataService.metadata;
  private readonly viewportEl = signal<HTMLElement | undefined>(undefined);
  private readonly containerWidth = signal<number>(0);
  private readonly layoutChanges = signal(0);
  private resizeObserver?: ResizeObserver;

  private timeoutActive: ReturnType<typeof setTimeout> | undefined;
  private isForwardPageTurn = false;
  private isJumpPending = false;
  private lastAllowedLocator: Locator | undefined;
  private rollbackTarget: Locator | undefined;
  private forwardGuard: ForwardNavigationGuard | undefined;
  private questionRollbackTarget: Locator | undefined;
  // Set while carrying out a navigation the reader confirmed, so the guard does not ask again.
  private isForwardConfirmed = false;

  readonly currentWindowIframe = signal<Window | undefined>(undefined);
  readonly canUseTwoColumns = computed(() => {
    const nav = this.navigator();
    if (!nav) return false;

    const minColWidth = ReaderNavigatorService.readiumMinColWidth(nav);
    const fontSize =
      this.navigatorPreferences()?.fontSize ?? ReaderNavigatorService.DEFAULT_FONT_SIZE;
    return this.containerWidth() >= ReaderNavigatorService.TWO_COLUMNS * minColWidth * fontSize;
  });
  readonly title = computed(() => this.chapterService.getTitleFromHref(this.actualLocator()?.href));
  /** Unrounded progress (0-100), for callers that need more than whole-percent resolution. */
  readonly exactProgress = computed(() => this.progressFromLocator(this.actualLocator()));
  readonly progress = computed(() => Math.round(this.exactProgress()));
  readonly initialPosition = resource({
    params: () => ({
      metadata: this.metadata(),
      isPreviewMode: this.metadataService.isPreviewMode(),
    }),
    loader: async ({ params }) => this.getLastLocation(params.metadata, params.isPreviewMode),
  });
  readonly navigator = computed(this.navigatorInitialize.bind(this));
  readonly navigatorPreferences = linkedSignal<EpubPreferences | undefined>(
    () => this.navigator()?.preferencesEditor.preferences,
  );
  readonly resourceLoaded = resource({
    params: () => this.navigator(),
    loader: this.loadNavigator.bind(this),
  });
  readonly actualLocator = signal<Locator | undefined>(undefined);
  /**
   * Resources currently on screen: every page of a fixed-layout spread (actualLocator only reports
   * the first one), or the current chapter of a reflowable book. Re-read on every committed position
   * and after layout changes Readium applies without reporting one (a fixed-layout spread switching
   * between one and two pages on resize or by preference).
   */
  readonly currentResourceHrefs = computed<string[]>(
    () => {
      this.layoutChanges();
      const nav = this.navigator();
      // Read before branching: the fixed-layout viewport is not reactive, page turns arrive through
      // actualLocator.
      const href = this.actualLocator()?.href;
      if (nav && this.metadataService.isFixedLayout()) return [...nav.viewport.readingOrder];
      return href ? [href] : [];
    },
    { equal: sameHrefs },
  );
  /** Positions committed by a jump (TOC, slider, search, bookmark...) rather than a page turn. */
  readonly jumpCount = signal(0);
  readonly selectionRect = signal<DOMRect | undefined>(undefined);
  readonly selectionEdit = signal<Highlight | undefined>(undefined);
  readonly _selectedText = signal<string | undefined>(undefined);
  readonly currentSelectionContext = signal<SelectionContext | undefined>(undefined);
  readonly fxlZoom = signal(ReaderNavigatorService.DEFAULT_FXL_ZOOM);

  constructor() {
    const annotationEffect = effect(
      () => {
        if (!this.resourceLoaded.hasValue() || !this.resourceLoaded.value()) return;
        const initialPosition = this.initialPosition.value();
        const initialWindow = this.currentWindowIframe();
        if (!initialPosition?.location || !initialWindow) return;
        annotationEffect.destroy();
      },
      { manualCleanup: true },
    );

    effect(() => {
      if (
        !this.resourceLoaded.hasValue() ||
        !this.resourceLoaded.value() ||
        this.metadataService.isPreviewMode() ||
        this.metadataService.isReadProgressDisabled()
      )
        return;
      const actualLocator = this.actualLocator();
      if (this.timeoutActive != undefined) {
        clearTimeout(this.timeoutActive);
      }
      this.timeoutActive = setTimeout(() => {
        this.readerLocationService.saveLastLocation(actualLocator, this.getCurrentFrameElements());
      }, ReaderNavigatorService.DEBOUNCE_SAVE_LAST_LOCATION);
    });

    effect(() => {
      if (this.metadataService.isFixedLayout()) return;
      const win = this.currentWindowIframe();
      this.actualLocator();
      const preferences = this.navigatorPreferences();
      const linkColor = preferences?.linkColor;
      const backgroundColor = preferences?.backgroundColor;
      const textColor = preferences?.textColor;
      if (!win || !linkColor || !backgroundColor || !textColor) return;
      const hoverColor = ReaderNavigatorService.resolveLinkHoverColor(linkColor);
      ReaderNavigatorService.injectContentStyles(win, { hoverColor, backgroundColor, textColor });
    });

    effect(() => {
      this.fullscreenService.isFullscreen();
      this.setFxlZoom(ReaderNavigatorService.DEFAULT_FXL_ZOOM);
    });
  }

  prev(): void {
    this.isJumpPending = false;
    this.navigator()?.goLeft(true, () => undefined);
  }

  next(): void {
    const proceed = (): void => {
      this.turnPageForward();
    };
    if (!this.allowsForward('pageTurn', proceed)) return;
    this.turnPageForward();
  }

  setForwardGuard(guard: ForwardNavigationGuard): void {
    this.forwardGuard = guard;
  }

  getCurrentFrameElements(): HTMLIFrameElement[] {
    return (
      this.navigator()
        ?._cframes.map((f) => f?.iframe)
        .filter((f): f is HTMLIFrameElement => f !== undefined) ?? []
    );
  }

  setFxlZoom(scale: number): void {
    const pool = this.navigator()?.pool;
    if (!pool || !ReaderNavigatorService.isFXLPool(pool)) return;
    pool.peripherals.scale = scale;
    pool.updateBookStyle();
    this.fxlZoom.set(scale);
  }

  get selectionContext(): Signal<SelectionContext | undefined> {
    return this.currentSelectionContext.asReadonly();
  }

  private navigatorInitialize(): EpubNavigator | undefined {
    const metadata = this.metadata();
    const viewportEl = this.viewportEl();
    const xBookId = this.metadataService.xBookId();
    if (
      !viewportEl ||
      !metadata ||
      this.initialPosition.isLoading() ||
      !xBookId ||
      !this.customFontsService.fontsLoaded()
    )
      return undefined;

    const baseUrl = new URL(metadata.readium.rootUrl, location.origin).href;
    const filesBaseUrl = new URL(bookFilesRootUrl(metadata), location.origin).href;

    const initialEpubConfiguration: EpubNavigatorConfiguration = {
      defaults: {},
      // Untracked: userSettings follows the viewport, which must not rebuild the navigator.
      preferences: untracked(() => this.userPreferences()) ?? {},
      injectables: this.customFontsService.injectablesConfig(),
      keyboardPeripherals: [
        {
          type: ReaderNavigatorService.NAV_PERIPHERAL_TYPE,
          keyCombos: [
            {
              keyCode: ReaderNavigatorService.KEY_CODE_ARROW_LEFT,
              suppressOnInteractiveElement: true,
            },
            {
              keyCode: ReaderNavigatorService.KEY_CODE_ARROW_RIGHT,
              suppressOnInteractiveElement: true,
            },
          ],
        },
      ],
    };

    const { links } = metadata.readium2.manifest;
    let linkSelf: Link | undefined = links?.findWithRel('self');
    if (linkSelf && links) {
      linkSelf = Link.deserialize({ ...linkSelf.serialize(), href: filesBaseUrl });
      if (linkSelf) links.items = [linkSelf];
    }

    const offlineBook = this.offlineSession.book();
    const offlineSource = offlineBook
      ? async (url: string): Promise<Response | undefined> =>
          this.bookContentCache.match(offlineBook.loanKey, url)
      : undefined;
    const inliner = offlineSource
      ? new OfflineResourceInliner(
          offlineSource,
          filesBaseUrl,
          new URL(bookFilesPrefix(metadata), location.origin).href,
        )
      : undefined;
    const fetcher = new CustomHttpFetcher(
      customFetch(this.authStore.token(), xBookId, offlineSource),
      baseUrl,
      {
        cipherKey: this.authStore.cipherKey(),
        transformChapter:
          inliner && (async (html, link): Promise<string> => inliner.inlineChapter(html, link)),
      },
    );

    const { manifest } = metadata.readium2;
    if (metadata.readium.rendition_layout === 'pre-paginated') {
      manifest.metadata.layout = Layout.fixed;
      if (!manifest.metadata.conformsTo?.length) {
        manifest.metadata.conformsTo = [Profile.EPUB];
      }
    }
    const publication = new Publication({ manifest, fetcher });
    const positions = metadata.readium2.positions.positions
      .map((pos) => Locator.deserialize(pos))
      .filter((l) => l !== undefined);

    return new EpubNavigator(
      viewportEl,
      publication,
      this.constructGetEventListeners(),
      positions,
      this.initialPosition.value()?.location,
      initialEpubConfiguration,
    );
  }

  private async loadNavigator({ params }: { params: EpubNavigator }): Promise<boolean> {
    const { resolve, promise } = Promise.withResolvers<boolean>();
    try {
      await params.load();

      const userSettings = this.metadataService.userSettings();
      const preferences = this.userPreferences();
      if (userSettings && preferences) {
        const matchedTheme = findReaderThemeByBackgroundColor(userSettings.backgroundColor);
        if (matchedTheme) {
          await applyDocumentTheme(matchedTheme.id);
        }
        // Already applied by the constructor, so this only syncs our state without a relayout.
        await this.updateReaderNavigatorPreferences(preferences);
      }

      const { location: initialPosition } = this.initialPosition.value() ?? {};
      if (initialPosition?.text)
        params.go(initialPosition, false, () => {
          resolve(true);
        });
      else resolve(true);
    } catch (err) {
      console.error('Error loading resource:', err);

      const positions = this.metadataService.positions();
      const initLocation = Locator.deserialize(positions[0]);
      if (!initLocation) return false;
      params.go(initLocation, false, () => {
        resolve(true);
      });
    }
    return promise;
  }

  /**
   * The reader's saved preferences. They are handed to the navigator when it is built: applied
   * after `load()` instead, they relay the chapter out once Readium has already restored the
   * position by its text, which then shifts to a neighbouring page.
   */
  private userPreferences(): IEpubPreferences | undefined {
    const userSettings = this.metadataService.userSettings();
    if (!userSettings) return undefined;
    const matchedTheme = findReaderThemeByBackgroundColor(userSettings.backgroundColor);
    const backgroundColor =
      matchedTheme?.backgroundColor ?? userSettings.backgroundColor ?? 'rgb(255, 255, 255)';
    return {
      fontFamily: userSettings.fontFamily ?? 'Lato',
      fontSize: userSettings.fontSize ?? ReaderNavigatorService.DEFAULT_FONT_SIZE,
      lineHeight: userSettings.lineHeight ?? ReaderNavigatorService.DEFAULT_LINE_HEIGHT,
      columnCount: userSettings.columnCount ?? ReaderNavigatorService.TWO_COLUMNS,
      backgroundColor,
      textColor: matchedTheme?.textColor ?? userSettings.textColor,
      linkColor: ReaderNavigatorService.resolveLinkColor(backgroundColor),
      // textAlign: userSettings.textAlign
      //   ? TextAlignment[userSettings.textAlign]
      //   : TextAlignment.start,
      // pageGutter: userSettings.pageGutter,
    };
  }

  load(viewportEl: HTMLElement): void {
    this.viewportEl.set(viewportEl);
    const parent = viewportEl.parentElement;
    if (!parent) return;
    this.containerWidth.set(parent.clientWidth);
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => {
      this.containerWidth.set(parent.clientWidth);
      this.notifyLayoutChange();
    });
    this.resizeObserver.observe(parent);
  }

  // Readium re-lays out fixed-layout spreads in its own requestAnimationFrame after a resize or a
  // preference change, without reporting a new position: wait for it before re-reading. Two frames
  // because our ResizeObserver is created in load(), before Readium's (the navigator is built
  // afterwards): on the next frame our first tick runs before Readium's resizeHandler, and the
  // second frame runs after it.
  private notifyLayoutChange(): void {
    let remaining = ReaderNavigatorService.LAYOUT_SETTLE_FRAMES;
    const tick = (): void => {
      remaining -= 1;
      if (remaining > 0) {
        requestAnimationFrame(tick);
        return;
      }
      this.layoutChanges.update((count) => count + 1);
    };
    requestAnimationFrame(tick);
  }

  private progressFromLocator(locator?: Locator): number {
    if (!locator) return ReaderNavigatorService.EMPTY_PROGRESS;
    if (!this.navigator()?.canGoForward) return ReaderNavigatorService.FULL_PROGRESS;
    return this.bookAnalysisService.calculateProgressFromLocator(locator);
  }

  async goToPercentage(percentage: number): Promise<void> {
    if (!this.isValidPercentage(percentage)) return;

    const targetLocator = this.bookAnalysisService.getLocatorForPercentage(percentage);
    if (targetLocator) {
      await this.goToLocator(targetLocator);
    }
  }

  private async getLastLocation(
    metadata: MetadataResponse | undefined,
    isPreviewMode: boolean,
  ): Promise<{ savedFromNR: boolean; location: Locator } | { location: undefined }> {
    if (!metadata || isPreviewMode) return Promise.resolve({ location: undefined });
    const { savedPlace } = metadata.userData;
    const location = this.readerLocationService.getLastLocation(savedPlace);
    if (!location) return { location };

    // Resuming beyond the paid boundary needs the same verdict as crossing it live. Below it
    // this short-circuits without a request, so the common case costs nothing on open.
    if (!(await this.paymentStatusService.authorize(savedPlace?.progress ?? 0))) {
      return {
        savedFromNR: false,
        location: this.paymentStatusService.thresholdLocator() ?? location,
      };
    }

    return {
      savedFromNR: savedPlace?.deviceId === 'web',
      location,
    };
  }

  async updateReaderNavigatorPreferences(preferences: IEpubPreferences): Promise<void> {
    const epubPreferences = new EpubPreferences(preferences);
    await this.navigator()?.submitPreferences(epubPreferences);
    this.navigatorPreferences.set(epubPreferences);
    this.notifyLayoutChange();
  }

  async goToLocator(locator: Locator): Promise<boolean> {
    const proceed = (): void => {
      void this.navigateToLocator(locator);
    };
    if (this.isForwardOfCurrent(locator) && !this.allowsForward('jump', proceed)) return false;
    return this.navigateToLocator(locator);
  }

  // `countsAsJump` is false when carrying out a confirmed swipe, which the reading stats must keep
  // counting as a page turn.
  private async navigateToLocator(locator: Locator, countsAsJump = true): Promise<boolean> {
    // The destination is known here, so a denied jump is simply abandoned: no rollback, no flicker.
    if (!(await this.paymentStatusService.authorize(this.rawProgress(locator)))) {
      this.isForwardConfirmed = false;
      return false;
    }
    const target = this.metadataService.isFixedLayout()
      ? ReaderNavigatorService.resourceOnly(locator)
      : locator;
    this.isJumpPending = countsAsJump;
    return new Promise((resolve) => this.navigator()?.go(target, true, resolve));
  }

  /**
   * Moves the current chapter frame to `progression` (a page start within the chapter), resolving
   * with the frame's ack. Fixed-layout frames never answer go_progression, so it is a no-op there.
   */
  async goToProgression(progression: number): Promise<boolean> {
    const frame = this.navigator()?._cframes[0];
    if (this.metadataService.isFixedLayout() || !frame?.msg) return false;
    const { msg } = frame;
    this.isJumpPending = true;
    return new Promise((resolve) => msg.send('go_progression', progression, resolve));
  }

  // Fixed-layout frames load no snapper, so they never answer go_text/go_id/go_progression and
  // Readium would wait forever: a fixed-layout page is a whole resource, its href is enough.
  private static resourceOnly(locator: Locator): Locator {
    return new Locator({
      href: locator.href,
      type: locator.type,
      locations: new LocatorLocations({}),
    });
  }

  async goToLink(link: Link): Promise<boolean> {
    const target = this.findFirstChapterPosition(link.href.split('#')[0]);
    const proceed = (): void => {
      void this.followLink(link, target);
    };
    if (target && this.isForwardOfCurrent(target) && !this.allowsForward('jump', proceed)) {
      return false;
    }
    return this.followLink(link, target);
  }

  private async followLink(link: Link, target: Locator | undefined): Promise<boolean> {
    if (target && !(await this.paymentStatusService.authorize(this.rawProgress(target)))) {
      this.isForwardConfirmed = false;
      return false;
    }
    this.isForwardPageTurn = false;
    this.isJumpPending = true;
    return new Promise<boolean>((resolve) => this.navigator()?.goLink(link, true, resolve));
  }

  private isValidPercentage(percentage: number): boolean {
    return Boolean(
      this.navigator() &&
        percentage >= ReaderNavigatorService.EMPTY_PROGRESS &&
        percentage <= ReaderNavigatorService.FULL_PROGRESS &&
        percentage !== this.progress(),
    );
  }

  private constructGetEventListeners(): EpubNavigatorListeners {
    return {
      frameLoaded: this.frameLoaded.bind(this),
      positionChanged: this.positionChanged.bind(this),
      tap: this.tap.bind(this),
      click: this.click.bind(this),
      zoom: this.zoomListener.bind(this),
      miscPointer: ReaderNavigatorService.miscPointer.bind(this),
      scroll: ReaderNavigatorService.scroll.bind(this),
      customEvent: ReaderNavigatorService.customEvent.bind(this),
      handleLocator: ReaderNavigatorService.handleLocator.bind(this),
      textSelected: this.textSelected.bind(this),
      contentProtection: ReaderNavigatorService.contentProtection.bind(this),
      contextMenu: ReaderNavigatorService.contextMenu.bind(this),
      peripheral: this.peripheral.bind(this),
      timelineItemChanged: ReaderNavigatorService.timelineItemChanged.bind(this),
      viewportChanged: ReaderNavigatorService.viewportChanged.bind(this),
    };
  }

  private frameLoaded(window: Window): void {
    this.currentWindowIframe.set(window);
  }

  private positionChanged(locator: Locator): void {
    const resolved = this.resolveIncomingLocator(locator);
    const rawProgress = this.rawProgress(resolved.locator);

    if (this.withholdForQuestions(resolved)) return;

    if (this.rollbackTarget) {
      // A rollback is in flight. Commit whatever Readium reports without re-arming the gate —
      // re-arming would attempt the payment again — and re-arm only once the reported position
      // is back below the threshold, which is how the rollback is known to have landed.
      if (!this.paymentStatusService.exceedsThreshold(rawProgress)) this.rollbackTarget = undefined;
      this.commitPosition(resolved);
      return;
    }

    if (this.paymentStatusService.mustWithhold(rawProgress)) {
      this.blockAndAuthorize(resolved);
      return;
    }

    this.commitPosition(resolved);
  }

  /**
   * Swipes reach Readium's column snapper inside the frame, so a page turn the guard refuses is
   * only known once it happened: send the reader back to the page they were on, as the payment
   * gate does. Returns whether the reported position was withheld.
   */
  private withholdForQuestions(resolved: ResolvedPosition): boolean {
    const target = this.questionRollbackTarget;
    if (target) {
      if (this.rawProgress(resolved.locator) <= this.rawProgress(target)) {
        this.questionRollbackTarget = undefined;
      }
      return false;
    }

    const current = this.actualLocator();
    if (!current || !this.isForwardOfCurrent(resolved.locator)) return false;
    if (this.isForwardConfirmed) {
      this.isForwardConfirmed = false;
      return false;
    }
    const proceed = (): void => {
      void this.navigateToLocator(resolved.locator, false);
    };
    if (this.allowsForward('pageTurn', proceed)) return false;

    this.questionRollbackTarget = current;
    setTimeout(() => {
      this.applyRollback(current, resolved.locator);
    }, ReaderNavigatorService.ROLLBACK_DELAY);
    return true;
  }

  private allowsForward(kind: 'pageTurn' | 'jump', proceed: () => void): boolean {
    const guard = this.forwardGuard;
    if (!guard) return true;
    return guard(kind, () => {
      this.isForwardConfirmed = true;
      proceed();
    });
  }

  private turnPageForward(): void {
    this.isJumpPending = false;
    this.isForwardPageTurn = true;
    this.navigator()?.goRight(true, () => undefined);
  }

  private isForwardOfCurrent(locator: Locator): boolean {
    const current = this.actualLocator();
    return current !== undefined && this.rawProgress(locator) > this.rawProgress(current);
  }

  /**
   * Normalises the position Readium reports without committing it, so the payment gate can
   * decide on the locator that would actually be displayed.
   */
  private resolveIncomingLocator(locator: Locator): ResolvedPosition {
    if (locator.href === this.actualLocator()?.href) {
      return { locator, chapterChanged: false, resetColumn: false };
    }

    const visibleIframe = findVisibleIframe(this.getCurrentFrameElements());
    if (visibleIframe?.contentWindow) {
      this.currentWindowIframe.set(visibleIframe.contentWindow);
      if (!ReaderNavigatorService.isTypingTarget(document.activeElement)) {
        visibleIframe.focus({ preventScroll: true });
      }
    }

    // FXL fix: changeResource(1) takes the first positions-list entry for the new
    // chapter, which may have progression=0.5 when the chapter is a right-hand page
    // in a 2-up spread. That causes getProgressPercentage to interpolate toward the
    // next chapter's totalProgression, producing a wildly wrong % (e.g. 48%).
    // Correct to progression=0 so the chapter start is displayed accurately.
    if (this.isForwardPageTurn && (locator.locations.progression ?? 0) > 0) {
      const corrected = this.findFirstChapterPosition(locator.href);
      const bestLocator =
        corrected && (corrected.locations.progression ?? 0) < (locator.locations.progression ?? 0)
          ? corrected
          : locator.copyWithLocations({ progression: 0 });
      return { locator: bestLocator, chapterChanged: true, resetColumn: true };
    }

    return { locator, chapterChanged: true, resetColumn: false };
  }

  private commitPosition(resolved: ResolvedPosition): void {
    // Consume the flag only on a chapter transition — same-chapter re-fires (e.g.
    // the "sync" pong that Readium emits before changeResource updates currentLocation)
    // must NOT clear it, or the flag won't be there for the real transition that follows.
    if (resolved.chapterChanged) this.isForwardPageTurn = false;

    this.actualLocator.set(resolved.locator);
    if (this.isJumpPending) {
      this.isJumpPending = false;
      this.jumpCount.update((count) => count + 1);
    }
    this.selectionRect.set(undefined);
    this.currentSelectionContext.set(undefined);

    if (!this.paymentStatusService.exceedsThreshold(this.rawProgress(resolved.locator))) {
      this.lastAllowedLocator = resolved.locator;
    }

    if (resolved.resetColumn) {
      // Reset the iframe column to position 0 so subsequent forward navigation steps through
      // the chapter from the beginning rather than from the mid-chapter position Readium remembered.
      this.navigator()?._cframes[0]?.msg?.send(
        'go_progression',
        CHAPTER_START_PROGRESSION,
        () => undefined,
      );
    }
  }

  /** Unrounded progress: progress() reports 100 at the end of the book, which would block there. */
  private rawProgress(locator: Locator): number {
    return this.bookAnalysisService.calculateProgressFromLocator(locator);
  }

  private blockAndAuthorize(resolved: ResolvedPosition): void {
    const target = resolved.locator;
    const rollback = this.lastAllowedLocator ?? this.paymentStatusService.thresholdLocator();

    if (!rollback) {
      // Nowhere safe to send the reader back to: let the position stand and let the dialog decide.
      void this.paymentStatusService.authorize(this.rawProgress(target));
      this.commitPosition(resolved);
      return;
    }

    this.rollbackTarget = rollback;
    // positionChanged runs inside Readium's syncLocation, which keeps mutating currentLocation
    // and awaits framePool.update after we return, so navigating re-entrantly is lost.
    setTimeout(() => {
      this.applyRollback(rollback, target);
    }, ReaderNavigatorService.ROLLBACK_DELAY);

    void this.paymentStatusService.authorize(this.rawProgress(target)).then(async (allowed) => {
      this.rollbackTarget = undefined;
      if (!allowed) return;
      await this.goToLocator(target);
    });
  }

  private applyRollback(target: Locator, blockedFrom: Locator): void {
    const nav = this.navigator();
    if (!nav) return;

    // Decided from the blocked destination, not from actualLocator: the gate withholds the
    // commit, so actualLocator still points at the rollback target and would always look like
    // the same resource. Readium has already swapped the frame pool to the blocked chapter, so
    // nudging the column only lands when the block stayed inside one resource.
    if (blockedFrom.href === target.href) {
      nav._cframes[0]?.msg?.send(
        'go_progression',
        Math.max(target.locations.progression ?? 0, CHAPTER_START_PROGRESSION),
        () => undefined,
      );
      return;
    }

    nav.go(target, false, () => undefined);
  }

  private findFirstChapterPosition(href: string): Locator | undefined {
    const metadata = this.metadata();
    if (!metadata) return undefined;
    return metadata.readium2.positions.positions
      .map((p) => Locator.deserialize(p))
      .filter((l): l is Locator => l !== undefined && l.href === href)
      .reduce<Locator | undefined>(
        (min, cur) =>
          !min || (cur.locations.progression ?? 0) < (min.locations.progression ?? 0) ? cur : min,
        undefined,
      );
  }

  private static readiumMinColWidth(nav: EpubNavigator): number {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-type-assertion
    const mll = (nav as any)._css?.lineLengths?.minimalLineLength;
    return typeof mll === 'number' ? mll : ReaderNavigatorService.MIN_SINGLE_COL_WIDTH;
  }

  private tap(e: FrameClickEvent): boolean {
    this.hideReaderElements();
    // Returning true claims the tap, which stops Readium from turning the page itself.
    const proceed = (): void => {
      this.turnPageForward();
    };
    return this.isForwardEdgeTap(e) && !this.allowsForward('pageTurn', proceed);
  }

  private isForwardEdgeTap(e: FrameClickEvent): boolean {
    const framesWidth = (this.navigator()?._cframes ?? []).reduce(
      (width, frame) => width + (frame?.iframe.contentWindow?.innerWidth ?? 0),
      0,
    );
    const slice = (framesWidth * window.devicePixelRatio) / ReaderNavigatorService.EDGE_TAP_SLICES;
    return e.x > slice * (ReaderNavigatorService.EDGE_TAP_SLICES - 1);
  }

  private click(_e: FrameClickEvent): boolean {
    ReaderNavigatorService.relayClick();
    this.hideReaderElements();
    // Clear selection to hide the selection toolbar
    this.selectionRect.set(undefined);
    const selection = this.currentWindowIframe()?.getSelection();
    if (!selection || selection.isCollapsed) {
      this.currentSelectionContext.set(undefined);
    }
    return true;
  }

  private hideReaderElements(): void {
    this.controlsVisibility.endInteraction('navigation-buttons', 0);
    this.controlsVisibility.endInteraction('header-footer', 0);
  }

  private zoomListener(scale: number): void {
    this.fxlZoom.set(scale);
  }

  private static miscPointer(amount: number): void {
    console.log('miscPointer', amount);
  }

  private static scroll(delta: number): void {
    console.log('scroll', delta);
  }

  private static customEvent(key: string, data: unknown): void {
    console.log('customEvent', key, data);
  }

  private static contentProtection(): void {
    return;
  }

  private static contextMenu(): void {
    return;
  }

  private static timelineItemChanged(): void {
    return;
  }

  private static viewportChanged(): void {
    return;
  }

  private peripheral(data: KeyboardPeripheralEventData): void {
    if (ReaderNavigatorService.isDialogOpen()) return;
    if (data.key === 'ArrowRight') {
      this.next();
    } else if (data.key === 'ArrowLeft') {
      this.prev();
    }
  }

  private static isDialogOpen(): boolean {
    return document.querySelector('odl-dialog[open]') !== null;
  }

  private static relayClick(): void {
    // Relay the click event to the main DOM to allow popovers to close and unify behaviors
    const iframes = Array.from(
      document.querySelectorAll<HTMLIFrameElement>('.reader__content iframe'),
    );
    const activeIframe = iframes.find((iframe) => iframe.style.visibility !== 'hidden');

    if (activeIframe) {
      activeIframe.dispatchEvent(new MouseEvent('click'));
    }
  }

  private static handleLocator(locator: Locator): boolean {
    const { href } = locator;
    if (
      href.startsWith('http://') ||
      href.startsWith('https://') ||
      href.startsWith('mailto:') ||
      href.startsWith('tel:')
    ) {
      if (confirm(`Open "${href}" ?`)) window.open(href, '_blank');
    } else {
      console.warn('Unhandled locator', locator);
    }
    return false;
  }

  private static resolveLinkColor(backgroundColor: string | undefined): string {
    const fromTheme = getComputedStyle(document.documentElement)
      .getPropertyValue('--link-initial-color')
      .trim();
    if (fromTheme) return fromTheme;
    return (
      findReaderThemeByBackgroundColor(backgroundColor)?.linkColor ??
      ReaderNavigatorService.LINK_COLOR_LIGHT
    );
  }

  private static resolveLinkHoverColor(linkColor: string): string {
    const fromTheme = getComputedStyle(document.documentElement)
      .getPropertyValue('--link-hover-color')
      .trim();
    return fromTheme || linkColor;
  }

  private static injectContentStyles(
    win: Window,
    colors: { backgroundColor: string; hoverColor: string; textColor: string },
  ): void {
    const { hoverColor, backgroundColor, textColor } = colors;
    const { head } = win.document;
    let style = head.querySelector<HTMLStyleElement>(`#${ReaderNavigatorService.CONTENT_STYLE_ID}`);
    if (!style) {
      style = win.document.createElement('style');
      style.id = ReaderNavigatorService.CONTENT_STYLE_ID;
      head.appendChild(style);
    }
    style.textContent = [
      `a[href] { text-decoration: underline !important; }`,
      `:root a[href]:hover { color: ${hoverColor} !important; }`,
      `::selection { background-color: ${textColor} !important; color: ${backgroundColor} !important; }`,
      `::-moz-selection { background-color: ${textColor} !important; color: ${backgroundColor} !important; }`,
    ].join('\n');
  }

  private static isFXLPool(pool: object): pool is FXLFramePoolManager {
    return 'peripherals' in pool && 'updateBookStyle' in pool;
  }

  // True when the user is actively typing, so we must not steal focus (e.g. the search box).
  private static isTypingTarget(element: Element | null): boolean {
    return (
      element instanceof HTMLInputElement ||
      element instanceof HTMLTextAreaElement ||
      element instanceof HTMLSelectElement ||
      (element instanceof HTMLElement && element.isContentEditable)
    );
  }

  private textSelected(selection: BasicTextSelection): void {
    this.selectionEdit.set(undefined);

    const currentFrameWindow = this.currentWindowIframe();
    const iframeElement = currentFrameWindow?.frameElement;

    if (!currentFrameWindow || !(iframeElement && isIframe(iframeElement))) {
      return;
    }

    const currentSelection = currentFrameWindow.getSelection();

    if (currentSelection?.isCollapsed) {
      this.selectionRect.set(undefined);
      this.currentSelectionContext.set(undefined);
      return;
    }

    const currentSelectionContext = getSelectionContext(
      ReaderNavigatorService.CONTEXT_NUM_WORDS,
      currentSelection,
    );
    if (!currentSelectionContext) {
      this.selectionRect.set(undefined);
      this.currentSelectionContext.set(undefined);
      return;
    }
    this.currentSelectionContext.set(currentSelectionContext);

    const iframePosition = iframeElement.getBoundingClientRect();

    this.selectionRect.set(
      DOMRect.fromRect({
        x: selection.x + iframePosition.left,
        y: selection.y + iframePosition.top,
        width: selection.width,
        height: selection.height,
      }),
    );
  }
}
